import { storageSize } from '@wr/shared';
import { get } from '../api.js';
import type { LocalStore } from './store.js';

/**
 * Content kept on this device for shows without network (round 2): the
 * offline copies of videos (MP4) and the PDFs, with their thumbnails, in
 * the browser's cache, under addresses the service worker answers without
 * network (videos with their byte ranges, so they can be skipped through).
 * Each person keeps their own list, on each device (in their encrypted
 * store); nothing is ever downloaded again without them asking (roaming).
 */
export const MEDIA_CACHE = 'wr-offline-media';
const META = 'offlineMedia';

/** What a device needs to know of a library item. */
export interface OfflineAsset {
  id: string;
  format: 'video' | 'pdf';
  title: string;
  has_file?: boolean;
  has_thumbnail?: boolean;
  has_offline?: boolean;
  size_bytes?: number | null;
  offline_size_bytes?: number | null;
  offline_version?: string | null;
  file_version?: string | null;
}

export interface KeptItem {
  id: string;
  format: 'video' | 'pdf';
  title: string;
  version: string;
  size: number;
  keptAt: string;
  /** The library changed it: a new version, its size (downloaded only when the person asks). */
  update?: { version: string; size: number } | null;
}

export type OfflineState = 'ready' | 'update' | 'none' | 'unavailable';

export const mediaUrl = (userId: string, id: string) => `/offline-media/${userId}/${id}`;
export const thumbUrl = (userId: string, id: string) => `/offline-media/${userId}/${id}/thumbnail`;

/** The version and size of what a device would keep of an item; null when it cannot be kept. */
export function offlineVersion(a: OfflineAsset): { version: string; size: number } | null {
  if (a.format === 'video') {
    return a.has_offline && a.offline_version
      ? { version: a.offline_version, size: Number(a.offline_size_bytes ?? 0) }
      : null;
  }
  return a.has_file !== false
    ? { version: a.file_version ?? 'pdf', size: Number(a.size_bytes ?? 0) }
    : null;
}

export async function keptItems(store: LocalStore): Promise<Record<string, KeptItem>> {
  return (await store.getMeta<Record<string, KeptItem>>(META)) ?? {};
}

async function saveKept(store: LocalStore, items: Record<string, KeptItem>) {
  await store.setMeta(META, items);
}

/** Ready, update available, not kept, or cannot be kept (a video without its offline copy). */
export function offlineState(a: OfflineAsset, kept: Record<string, KeptItem>): OfflineState {
  const item = kept[a.id];
  if (item) return item.update ? 'update' : 'ready';
  return offlineVersion(a) ? 'none' : 'unavailable';
}

/** The space this site uses and may use on this device, and whether the browser keeps it. */
export async function storageInfo(): Promise<{
  usage: number | null;
  quota: number | null;
  persisted: boolean | null;
}> {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;
  const estimate = await storage?.estimate?.().catch(() => undefined);
  const persisted = await storage?.persisted?.().catch(() => null);
  return {
    usage: estimate?.usage ?? null,
    quota: estimate?.quota ?? null,
    persisted: persisted ?? null,
  };
}

/** Asks the browser to keep this site's data; false when it refuses (or cannot say). */
export async function askPersistent(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

/** Why a download cannot start: not enough space (with what is needed and free). */
export async function spaceProblem(needed: number): Promise<string | null> {
  const { usage, quota } = await storageInfo();
  if (usage === null || quota === null) return null;
  const free = Math.max(0, quota - usage);
  // A margin: the browser counts more than the files themselves.
  return needed * 1.1 > free
    ? `Not enough space on this device: ${storageSize(needed)} needed, ${storageSize(free)} free. Remove some offline content, or free space on the device.`
    : null;
}

async function cacheBody(url: string, key: string, type: string, onBytes: (n: number) => void) {
  const res = await fetch(url, url.startsWith('/') ? { credentials: 'same-origin' } : {});
  if (!res.ok || !res.body) throw new Error(`download ${res.status}`);
  let seen = 0;
  const counting = res.body.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        seen += chunk.byteLength;
        onBytes(seen);
        controller.enqueue(chunk);
      },
    }),
  );
  const cache = await caches.open(MEDIA_CACHE);
  const length = res.headers.get('content-length');
  await cache.put(
    key,
    new Response(counting, {
      headers: { 'content-type': type, ...(length ? { 'content-length': length } : {}) },
    }),
  );
  return seen;
}

/** Where a device downloads an item from: the library, or a booth kiosk's own addresses. */
export interface MediaSources {
  /** The API path that answers a signed address of the video's offline copy. */
  copy(id: string): string;
  file(id: string): string;
  thumbnail(id: string): string;
}
const LIBRARY: MediaSources = {
  copy: (id) => `/content/${id}/offline/file`,
  file: (id) => `/api/content/${id}/file`,
  thumbnail: (id) => `/api/content/${id}/thumbnail`,
};
export const kioskSources = (eventId: string): MediaSources => ({
  copy: (id) => `/kiosk/events/${eventId}/assets/${id}/offline`,
  file: (id) => `/api/kiosk/events/${eventId}/assets/${id}/file`,
  thumbnail: (id) => `/api/kiosk/events/${eventId}/assets/${id}/thumbnail`,
});

/**
 * Keeps an item on this device (or brings it up to date): a video's offline
 * copy straight from Spaces, a PDF from the portal, and its thumbnail.
 */
export async function keepItem(
  store: LocalStore,
  userId: string,
  a: OfflineAsset,
  onBytes: (n: number) => void = () => undefined,
  from: MediaSources = LIBRARY,
): Promise<KeptItem> {
  const target = offlineVersion(a);
  if (!target) throw new Error('not available offline');
  if (typeof caches === 'undefined') throw new Error('no cache');
  let size: number;
  if (a.format === 'video') {
    const { url } = await get<{ url: string }>(from.copy(a.id));
    size = await cacheBody(url, mediaUrl(userId, a.id), 'video/mp4', onBytes);
  } else {
    size = await cacheBody(from.file(a.id), mediaUrl(userId, a.id), 'application/pdf', onBytes);
  }
  if (a.has_thumbnail) {
    await cacheBody(
      from.thumbnail(a.id),
      thumbUrl(userId, a.id),
      'image/jpeg',
      () => undefined,
    ).catch(() => undefined);
  }
  const item: KeptItem = {
    id: a.id,
    format: a.format,
    title: a.title,
    version: target.version,
    size,
    keptAt: new Date().toISOString(),
    update: null,
  };
  const kept = await keptItems(store);
  await saveKept(store, { ...kept, [a.id]: item });
  return item;
}

/** Removes an item from this device. */
export async function dropItem(store: LocalStore, userId: string, id: string): Promise<void> {
  if (typeof caches !== 'undefined') {
    const cache = await caches.open(MEDIA_CACHE);
    await cache.delete(mediaUrl(userId, id));
    await cache.delete(thumbUrl(userId, id));
  }
  const kept = await keptItems(store);
  delete kept[id];
  await saveKept(store, kept);
}

export async function dropAll(store: LocalStore, userId: string): Promise<void> {
  for (const id of Object.keys(await keptItems(store))) await dropItem(store, userId, id);
}

/**
 * The library's rules, at each look at the library online: an item deleted,
 * or whose offline copy was removed, leaves this device; a new version is
 * only noted ("Update available" with its size), never downloaded by itself.
 */
export async function reconcile(
  store: LocalStore,
  userId: string,
  library: OfflineAsset[],
  /** Only some items (a booth kiosk's): the others are left as they are. */
  some = false,
): Promise<Record<string, KeptItem>> {
  const kept = await keptItems(store);
  const byId = new Map(library.map((a) => [a.id, a]));
  let changed = false;
  for (const item of Object.values(kept)) {
    if (some && !byId.has(item.id)) continue;
    const a = byId.get(item.id);
    const target = a ? offlineVersion(a) : null;
    if (!a || !target) {
      await dropItem(store, userId, item.id);
      delete kept[item.id];
      changed = true;
      continue;
    }
    const update = target.version !== item.version ? target : null;
    if (JSON.stringify(update) !== JSON.stringify(item.update ?? null) || a.title !== item.title) {
      kept[item.id] = { ...item, title: a.title, update };
      changed = true;
    }
  }
  if (changed) await saveKept(store, kept);
  return kept;
}

/**
 * What a set of items needs on this device (a booth kiosk, a show): what is
 * missing or has an update, its size, and the videos that cannot be kept.
 */
export function preparation(items: OfflineAsset[], kept: Record<string, KeptItem>) {
  const todo = items.filter((a) => ['none', 'update'].includes(offlineState(a, kept)));
  return {
    todo,
    size: todo.reduce((n, a) => n + (offlineVersion(a)?.size ?? 0), 0),
    noCopy: items.filter((a) => offlineState(a, kept) === 'unavailable').length,
    ready: todo.length === 0,
  };
}

/** A kept thumbnail, to show without network; null when there is none. */
export async function keptImage(userId: string, id: string): Promise<string | null> {
  if (typeof caches === 'undefined') return null;
  const res = await caches.match(thumbUrl(userId, id), { cacheName: MEDIA_CACHE });
  return res ? URL.createObjectURL(await res.blob()) : null;
}

/** A kept file's bytes (a PDF to show), else null. */
export async function keptBytes(userId: string, id: string): Promise<ArrayBuffer | null> {
  if (typeof caches === 'undefined') return null;
  const res = await caches.match(mediaUrl(userId, id), { cacheName: MEDIA_CACHE });
  return res ? res.arrayBuffer() : null;
}

/**
 * The address a video plays from without network: the service worker's
 * (byte ranges), else, without it (development), the file itself in memory.
 */
export async function playableUrl(userId: string, id: string): Promise<string | null> {
  if (typeof caches === 'undefined') return null;
  const res = await caches.match(mediaUrl(userId, id), { cacheName: MEDIA_CACHE });
  if (!res) return null;
  if (navigator.serviceWorker?.controller) return mediaUrl(userId, id);
  return URL.createObjectURL(await res.blob());
}

/** An iPhone or an iPad (iPadOS says it is a Mac, with a touch screen). */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

/** Opened as the installed app (home screen), not in a browser tab. */
export function isInstalledApp(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** On an iPhone, Safari and the installed app do not share what they keep (decision of Oct 30, 2026). */
export const IPHONE_REMINDER =
  'On an iPhone or an iPad, prepare offline content in the installed app (Waste Robotics on the home screen), not in Safari: they do not share what they keep, and only the installed app is protected from being cleared.';
